import type { ReactElement } from "react";
import { Link } from "react-router-dom";
import { resourceArticles } from "../../Resources/components/newborn-pages/data/resourceArticles";
import Styles from "./newbornPricing.module.scss";

export type PricingGuideCategory = "newborn" | "baby" | "maternity" | "family";

const categoryLabels: Record<PricingGuideCategory, string> = {
  newborn: "newborn",
  baby: "baby",
  maternity: "maternity",
  family: "family",
};

const preferredGuideSlugs: Partial<Record<PricingGuideCategory, string[]>> = {
  newborn: ["in-home-newborn-photography", "newborn-photography-safety", "how-to-prepare-for-your-newborn-session"],
};

interface PricingGuideCenterProps {
  category: PricingGuideCategory;
}

export const PricingGuideCenter = ({ category }: PricingGuideCenterProps): ReactElement => {
  const categoryLabel = categoryLabels[category];
  const categoryGuides = resourceArticles.filter((article) => article.category === category);
  const preferredSlugs = preferredGuideSlugs[category];
  const guides = preferredSlugs
    ? preferredSlugs
        .map((slug) => categoryGuides.find((article) => article.slug === slug))
        .filter((article): article is (typeof categoryGuides)[number] => article !== undefined)
    : categoryGuides.slice(0, 3);

  return (
    <section className={Styles.pricingGuides} aria-labelledby={`${category}-planning-guides-title`}>
      <header className={Styles.pricingGuides__header}>
        <p className={Styles.newbornPricing__eyebrow}>Plan with confidence</p>
        <h2 id={`${category}-planning-guides-title`}>Helpful {categoryLabel} session guides</h2>
        <p>Answers to the questions families usually ask before choosing a package.</p>
      </header>

      <div className={Styles.pricingGuides__grid}>
        {guides.map((guide, index) => (
          <Link
            aria-label={`Read ${guide.title}`}
            className={Styles.pricingGuides__card}
            key={`${guide.category}-${guide.slug}`}
            to={`/resources/${guide.category}/${guide.slug}/`}
          >
            <span aria-hidden="true" className={Styles.pricingGuides__number}>
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className={Styles.pricingGuides__label}>{guide.eyebrow}</span>
            <strong>{guide.title}</strong>
            <span className={Styles.pricingGuides__description}>{guide.description}</span>
            <span className={Styles.pricingGuides__link}>
              Read the guide <span aria-hidden="true">→</span>
            </span>
          </Link>
        ))}
      </div>

      <Link className={Styles.pricingGuides__all} to={`/resources/${category}/`}>
        Explore all {categoryLabel} guides <span aria-hidden="true">→</span>
      </Link>
    </section>
  );
};

export default PricingGuideCenter;
